import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useActor } from '@/auth/pin/phoneSession'
import { useFormat, useI18n } from '@/i18n'
import { newId } from '@/lib/ids'
import { enqueueCommand } from '@/offline'
import {
  Banner,
  BilingualLabel,
  Button,
  Card,
  CardBody,
  CardHeader,
  ChoiceGroup,
  EmptyState,
  Field,
  IntegerInput,
  ScanField,
  useToast,
  type ScanMeta,
} from '@/ui'
import type { CloseRunPayload, RecordOutputPayload } from '@/features/process/commands'
import '@/features/process/commands'
import { isRunLive, liveOutputs, runParentIds, type ProcessState, type RunView } from '@/features/process/projection'
import { checkFreshCode, needsSecondEntry, runFigures } from '@/features/process/model/rules'
import { Line, ReadingField, type Reading } from '@/features/process/components/bits'
import { useBoth, useRefusal } from '@/features/process/components/useText'
import { ProcessScreen } from '@/features/process/components/ProcessScreen'

/**
 * F7-B1.c — Empty held pouches into new ordinary sacks (red tie, new tag, weighed), count the
 * pouches emptied and destroyed whole and the film destroyed, then close the run. Our label is
 * never reused; the film is counted and destroyed.
 */
export function DelabelPage() {
  const { runId = '' } = useParams()
  return (
    <ProcessScreen title="pack.delabel.title" backTo="/m/pack">
      {(state) => {
        const run = state.runs.get(runId)
        return run && run.step_code === 'DELABEL' ? <Delabel key={run.id} state={state} run={run} /> : <Missing />
      }}
    </ProcessScreen>
  )
}

function Missing() {
  const { t } = useI18n()
  return <EmptyState title={t('pack.run.missing')} />
}

function Delabel({ state, run }: { state: ProcessState; run: RunView }) {
  const { t } = useI18n()
  const fmt = useFormat()
  const toast = useToast()
  const actor = useActor()
  const refusal = useRefusal()
  const both = useBoth()
  const step = state.steps.get('DELABEL')
  const outKey = step?.outputs[0]?.key ?? 'EMPTIED'
  const pouches = Number(run.params.pouches ?? 0)
  const live = liveOutputs(run)
  const defaultSack = state.pack.sack_types.find((s) => s.default_output) ?? state.pack.sack_types[0]
  const [sackId, setSackId] = useState<string | null>(defaultSack?.id ?? null)
  const [tag, setTag] = useState<{ code: string; entry: ScanMeta['entry'] } | null>(null)
  const [reading, setReading] = useState<Reading | null>(null)
  const [formKey, setFormKey] = useState(0)
  const [emptied, setEmptied] = useState<number | null>(null)
  const [destroyed, setDestroyed] = useState<number | null>(null)
  const [film, setFilm] = useState<number | null>(null)
  const [saving, setSaving] = useState(false)
  const sack = state.pack.sack_types.find((s) => s.id === sackId)

  if (!isRunLive(run.status))
    return <Banner tone="success">{t('pack.delabel.closed')}</Banner>

  const saveSack = async () => {
    if (!tag || !reading || !sack) return
    try {
      const container_id = newId()
      const payload: RecordOutputPayload = {
        container_id,
        unit_id: live[0]?.unit_id ?? newId(),
        run_id: run.id,
        qr_payload: tag.code,
        entry: tag.entry,
        stream_key: outKey,
        grade: null,
        sack_type_id: sack.id,
        tare_g: sack.tare_g,
        weight_read_g: reading.read_g,
        confirmed_twice: reading.confirmed_twice,
        moisture_pct: null,
        tie: 'CONV',
      }
      await enqueueCommand({ id: container_id, type: 'record_output', payload, actorId: actor.user_id, parentIds: runParentIds(run) })
      setTag(null)
      setReading(null)
      setFormKey((n) => n + 1)
      toast.success(t('pack.delabel.sackSaved'))
    } catch (e) {
      toast.error(t('process.saveFailed'), e instanceof Error ? e.message : undefined)
    }
  }

  const counted = (emptied ?? 0) + (destroyed ?? 0)
  const countsOk = emptied !== null && destroyed !== null && film !== null && counted === pouches && film >= (emptied ?? 0)
  const ready = countsOk && ((emptied ?? 0) === 0 || live.length > 0)

  const close = async () => {
    if (!ready) return
    setSaving(true)
    try {
      const fig = runFigures(run, step)
      const payload: CloseRunPayload = {
        run_id: run.id,
        ended_at: new Date().toISOString(),
        zero_streams: live.length ? [] : [outKey],
        params: { pouches_emptied: emptied!, pouches_destroyed: destroyed!, film_destroyed: film! },
        phone_totals: {
          input_g: fig.input_g,
          output_g: live.reduce((a, o) => a + o.net_g, 0),
          bags_in: 0,
          sacks_out: live.length,
        },
      }
      await enqueueCommand({ type: 'close_run', payload, actorId: actor.user_id, parentIds: runParentIds(run) })
      toast.success(t('pack.delabel.done'))
    } catch (e) {
      toast.error(t('process.saveFailed'), e instanceof Error ? e.message : undefined)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardBody className="py-1">
          <Line label={t('pack.close.lotCode')} value={String(run.params.pack_lot_code ?? '—')} />
          <Line label={t('pack.delabel.toEmpty')} value={pouches} strong />
        </CardBody>
      </Card>
      <Banner tone="error">{both('pack.delabel.rule')}</Banner>

      <Card key={formKey}>
        <CardHeader eyebrow={<BilingualLabel k="pack.delabel.newSack" />} />
        <CardBody className="flex flex-col gap-3">
          <Field label={<BilingualLabel k="process.weighOut.tag" />} required>
            <ScanField
              value={tag?.code ?? null}
              validate={(c) => {
                const v = checkFreshCode(state, c, 'OUTPUT_BAG', actor.user_id)
                return v.ok ? null : refusal(v.reason)
              }}
              onChange={(c, meta) => setTag(c ? { code: c.trim().toUpperCase(), entry: meta?.entry ?? 'SCAN' } : null)}
            />
          </Field>
          <Field label={<BilingualLabel k="process.weighOut.sackType" />} required>
            <ChoiceGroup
              layout="stack"
              options={state.pack.sack_types.map((s) => ({ value: s.id, label: `${s.name} · ${fmt.weight(s.tare_g)}` }))}
              value={sackId}
              onChange={setSackId}
            />
          </Field>
          <ReadingField
            label={<BilingualLabel k="process.weighOut.weight" />}
            needsTwice={(g) => needsSecondEntry(g, state.pack.settings)}
            onChange={setReading}
          />
          <Button size="touch" disabled={!tag || !reading || !sack || reading.read_g <= sack.tare_g} onClick={() => void saveSack()}>
            <BilingualLabel k="process.weighOut.save" />
          </Button>
        </CardBody>
      </Card>
      {live.length > 0 && (
        <Card>
          <CardBody className="py-1">
            {live.map((o) => (
              <Line key={o.container_id} label={o.qr_payload} value={fmt.weight(o.net_g)} />
            ))}
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader eyebrow={<BilingualLabel k="pack.delabel.counts" />} />
        <CardBody className="flex flex-col gap-3">
          <Field label={<BilingualLabel k="pack.delabel.emptied" />} required>
            <IntegerInput value={emptied} onChange={setEmptied} />
          </Field>
          <Field label={<BilingualLabel k="pack.delabel.destroyedWhole" />} required>
            <IntegerInput value={destroyed} onChange={setDestroyed} />
          </Field>
          <Field label={<BilingualLabel k="pack.delabel.film" />} hint={t('pack.delabel.filmHint')} required>
            <IntegerInput value={film} onChange={setFilm} />
          </Field>
          {emptied !== null && destroyed !== null && counted !== pouches && (
            <Banner tone="warn">{t('pack.delabel.countGap', { n: counted, m: pouches })}</Banner>
          )}
        </CardBody>
      </Card>
      <Button size="xl" fullWidth disabled={!ready} loading={saving} onClick={() => void close()}>
        <BilingualLabel k="process.close.button" />
      </Button>
    </div>
  )
}
